import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Spinner } from '../ui/spinner';
import { toast } from '../ui/sonner';
import { ApiError } from '../../lib/auth/client';
import { updateCredentialMultiplier } from '../../lib/queries/credentials';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credentialId: string;
  label: string;
  currentMultiplier: string;
}

export function MultiplierDialog({ open, onOpenChange, credentialId, label, currentMultiplier }: Props) {
  const qc = useQueryClient();
  const [value, setValue] = useState(currentMultiplier);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(currentMultiplier);
      setError(null);
    }
  }, [open, currentMultiplier]);

  const update = useMutation({
    mutationFn: (multiplier: number) => updateCredentialMultiplier(credentialId, { usageMultiplier: multiplier }),
    onSuccess: () => {
      toast.success('Katsayı güncellendi');
      qc.invalidateQueries({ queryKey: ['credentials'] });
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Güncelleme başarısız');
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed < 0.01 || parsed > 100) {
      setError('Katsayı 0.01 ile 100 arasında olmalı');
      return;
    }
    update.mutate(parsed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Katsayı Güncelle</DialogTitle>
          <DialogDescription>
            <strong>{label}</strong> için kullanım katsayısını girin.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="multiplierValue">Kullanım Katsayısı</Label>
            <Input
              id="multiplierValue"
              type="number"
              step="0.01"
              min="0.01"
              max="100"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-slate-500">Varsayılan: 1.00 — maliyet bu katsayıyla çarpılarak gösterilir.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              İptal
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <><Spinner /> Kaydediliyor…</> : 'Kaydet'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
